// QuickActionCards.jsx
import React, { useState, useEffect } from 'react';
import { Grid, Card, CardContent, Typography, CircularProgress, Button, Avatar } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { connect } from 'react-redux';
import {
    SwapHoriz as SwapHorizIcon,
    TrendingUp as TrendingUpIcon,
    Assessment as AssessmentIcon,
    SkipNext as SkipNextIcon,
    Notifications as CircleNotificationsIcon
} from '@material-ui/icons';
import { 
    changeReadingModeUp,
    changeReadingModeDown,
    changeBuddyBooksLevelUp, 
    changeBuddyBooksLevelDown,
    acknowledgeQuickAction
} from '../../../../redux/actions/platform/books';

// Debug configuration
const DEBUG = true;
const log = (...args) => {
    if (DEBUG) {
        console.log('[QuickActionCards]', ...args);
    }
};

const styles = (theme) => ({
    root: {
        marginTop: theme.spacing(3),
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
    },
    card: {
        height: '100%',
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
        },
    },
    cardContent: {
        padding: `${theme.spacing(2)}px ${theme.spacing(3)}px !important`,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minHeight: 72,
    },
    avatar: {
        width: 48,
        height: 48,
        flexShrink: 0,
    },
    contentSection: {
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
    },
    title: {
        fontWeight: 'bold',
        marginBottom: theme.spacing(0.5),
        color: '#333',
        fontSize: '1.1rem',
        lineHeight: 1.2,
    },
    description: {
        color: '#666',
        fontSize: '0.9rem',
        lineHeight: 1.4,
        margin: 0,
    },
    buttonSection: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexShrink: 0,
    },
    actionButton: {
        textTransform: 'none',
        fontWeight: 'bold',
        borderRadius: 6,
        padding: '6px 16px',
    },
    doItButton: {
        backgroundColor: '#2196F3',
        color: 'white',
        '&:hover': {
            backgroundColor: '#1976D2',
        },
    },
    acknowledgeButton: {
        backgroundColor: '#757575',
        color: 'white',
        '&:hover': {
            backgroundColor: '#616161',
        },
    },
    takeToDataButton: {
        backgroundColor: '#f44336',
        color: 'white',
        '&:hover': {
            backgroundColor: '#d32f2f',
        },
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: theme.spacing(4),
    },
    error: {
        color: '#d32f2f',
        textAlign: 'center',
        padding: theme.spacing(2),
    },
    placeholder: {
        color: '#666',
        fontStyle: 'italic',
        textAlign: 'center',
        padding: theme.spacing(4),
    },
});

const QuickActionCards = ({ classes, quickActions, loading, error, dispatch }) => {
    const [actionLoading, setActionLoading] = useState({});
    const [actionErrors, setActionErrors] = useState({});
    const [visibleActions, setVisibleActions] = useState(quickActions || []);

    useEffect(() => {
        setVisibleActions(quickActions || []);
    }, [quickActions]);

    // Debug logging
    log('QuickActionCards received props:', {
        quickActions,
        quickActionsLength: quickActions ? quickActions.length : 'undefined',
        loading,
        error
    });

    // Parse markdown formatting in text
    const parseMarkdown = (text) => {
        if (!text) return text;
        
        const parts = [];
        let currentIndex = 0;
        let keyCounter = 0;
        
        // First handle bold text (**text**)
        const boldRegex = /\*\*(.*?)\*\*/g;
        let match;
        
        while ((match = boldRegex.exec(text)) !== null) {
            // Add text before the match
            if (match.index > currentIndex) {
                parts.push(text.slice(currentIndex, match.index));
            }
            
            // Add bold text
            parts.push(
                <strong key={`bold-${keyCounter++}`} style={{ fontWeight: 'bold' }}>
                    {match[1]}
                </strong>
            );
            
            currentIndex = match.index + match[0].length;
        }
        
        // Add remaining text
        if (currentIndex < text.length) {
            parts.push(text.slice(currentIndex));
        }
        
        // Now handle italic text (*text*) in the remaining string parts
        const finalParts = [];
        parts.forEach((part, partIndex) => {
            if (typeof part === 'string') {
                const italicRegex = /\*(.*?)\*/g;
                let italicMatch;
                let partCurrentIndex = 0;
                
                while ((italicMatch = italicRegex.exec(part)) !== null) {
                    // Add text before the match
                    if (italicMatch.index > partCurrentIndex) {
                        finalParts.push(part.slice(partCurrentIndex, italicMatch.index));
                    }
                    
                    // Add italic text
                    finalParts.push(
                        <em key={`italic-${keyCounter++}`} style={{ fontStyle: 'italic' }}>
                            {italicMatch[1]}
                        </em>
                    );
                    
                    partCurrentIndex = italicMatch.index + italicMatch[0].length;
                }
                
                // Add remaining text from this part
                if (partCurrentIndex < part.length) {
                    finalParts.push(part.slice(partCurrentIndex));
                }
            } else {
                // This is already a JSX element (bold text), keep it as is
                finalParts.push(part);
            }
        });
        
        return finalParts.length > 1 ? finalParts : finalParts[0] || text;
    };

    // Define action configurations
    const getActionConfig = (actionType) => {
        const configsByType = {
            // BOOK LEVEL UP -> Reading Level Progress
            'BOOK LEVEL UP': {
                icon: <TrendingUpIcon />,
                iconColor: '#4CAF50',
                iconBgColor: '#E8F5E8',
                buttonText: 'Do It',
                buttonClass: 'doItButton'
            },
            // BOOK LEVEL DOWN -> Reading Level Adjustment
            'BOOK LEVEL DOWN': {
                icon: <AssessmentIcon />,
                iconColor: '#FF9800',
                iconBgColor: '#FFF3E0',
                buttonText: 'Do It',
                buttonClass: 'doItButton'
            },
            // READING LEVEL UP/DOWN -> Reading Mode Adjustment
            'READING LEVEL UP': {
                icon: <SwapHorizIcon />,
                iconColor: '#4CAF50',
                iconBgColor: '#E8F5E8',
                buttonText: 'Do It',
                buttonClass: 'doItButton'
            },
            'READING LEVEL DOWN': {
                icon: <SwapHorizIcon />,
                iconColor: '#FF9800',
                iconBgColor: '#FFF3E0',
                buttonText: 'Do It',
                buttonClass: 'doItButton'
            },
            // SKIPRATE -> High Skip Rate (acknowledge)
            'SKIPRATE': {
                icon: <SkipNextIcon />,
                iconColor: '#FF9800',
                iconBgColor: '#FFF3E0',
                buttonText: 'Acknowledge',
                buttonClass: 'acknowledgeButton'
            },
            // OTHER -> NEW (acknowledge)
            'OTHER': {
                icon: <CircleNotificationsIcon />,
                iconColor: '#FFDD00',
                iconBgColor: '#FFF3E0',
                buttonText: 'Acknowledge',
                buttonClass: 'acknowledgeButton'
            }
        };

        return configsByType[actionType] || {
            icon: <AssessmentIcon />,
            iconColor: '#757575',
            iconBgColor: '#F5F5F5',
            buttonText: 'Action',
            buttonClass: 'doItButton'
        };
    };

    const handleActionClick = async (action, actionConfig) => {
        log('Quick Action clicked:', {
            type: action.type,
            name: action.name,
            description: action.description,
            studentId: action.studentId,
            date: action.date,
            acknowledged: action.acknowledged,
            buttonText: actionConfig.buttonText,
            key: action.key
        });

        const actionKey = `${action.type || action.name}_${action.studentId}`;
        
        // Optimistically remove this action from the visible list
        setVisibleActions(prev => prev.filter(a => (a && a.key && action && action.key) ? a.key !== action.key : a !== action));

        // Set loading state for this specific action
        setActionLoading(prev => ({ ...prev, [actionKey]: true }));
        setActionErrors(prev => ({ ...prev, [actionKey]: null }));

        try {
            let result;
            
            switch (action.type) {
                case 'READING LEVEL UP':
                    result = await dispatch(changeReadingModeUp(action.studentId));
                    break;
                case 'READING LEVEL DOWN':
                    result = await dispatch(changeReadingModeDown(action.studentId));
                    break;
                case 'BOOK LEVEL UP':
                    result = await dispatch(changeBuddyBooksLevelUp(action.studentId));
                    break;
                case 'BOOK LEVEL DOWN':
                    result = await dispatch(changeBuddyBooksLevelDown(action.studentId));
                    break;
                case 'SKIPRATE':
                case 'OTHER':
                    // These are acknowledgment actions - just log for now
                    log('Acknowledged action:', action.type || action.name);
                    result = { success: true, message: 'Action acknowledged' };
                    break;
                default:
                    break;
                    // throw new Error(`Unknown action type: ${action.type}`);
            }
            
            log('Quick Action completed successfully:', result);
            // Second action: acknowledge the quick action by key
            if (action && action.studentId && action.key) {
                try {
                    await dispatch(acknowledgeQuickAction(action.studentId, action.key));
                    log('Quick Action acknowledgment submitted successfully.');
                } catch (ackErr) {
                    console.error('Quick Action acknowledgment failed:', ackErr);
                }
            }
            
        } catch (err) {
            console.error('Quick Action failed:', err);
            setActionErrors(prev => ({ 
                ...prev, 
                [actionKey]: err.message || 'Action failed. Please try again.' 
            }));
        } finally {
            setActionLoading(prev => ({ ...prev, [actionKey]: false }));
        }
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className={classes.loading}>
                    <CircularProgress size={24} />
                    <Typography style={{ marginLeft: 16 }}>Loading quick actions...</Typography>
                </div>
            );
        }

        if (error) {
            return (
                <Typography className={classes.error}>
                    {error}
                </Typography>
            );
        }

        if (visibleActions && visibleActions.length > 0) {
            return (
                <Grid container spacing={3}>
                    {visibleActions.map((action, index) => {
                        const actionConfig = getActionConfig(action.type);
                        const actionKey = `${action.type || action.name}_${action.studentId}`;
                        const isLoading = actionLoading[actionKey];
                        const hasError = actionErrors[actionKey];
                        
                        return (
                            <Grid item xs={12} key={index}>
                                <Card className={classes.card}>
                                    <CardContent className={classes.cardContent}>
                                        <Avatar 
                                            className={classes.avatar}
                                            style={{ 
                                                backgroundColor: actionConfig.iconBgColor,
                                                color: actionConfig.iconColor 
                                            }}
                                        >
                                            {actionConfig.icon}
                                        </Avatar>
                                        
                                        <div className={classes.contentSection}>
                                            <Typography variant="h6" className={classes.title}>
                                                {action.name}
                                            </Typography>
                                            <Typography className={classes.description}>
                                                {parseMarkdown(action.description)}
                                            </Typography>
                                        </div>
                                        
                                        <div className={classes.buttonSection}>
                                            <Button
                                                className={`${classes.actionButton} ${classes[actionConfig.buttonClass]}`}
                                                onClick={() => handleActionClick(action, actionConfig)}
                                                disabled={isLoading}
                                            >
                                                {isLoading ? (
                                                    <CircularProgress size={16} color="inherit" />
                                                ) : (
                                                    actionConfig.buttonText
                                                )}
                                            </Button>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
                </Grid>
            );
        }

        return (
            <Typography className={classes.placeholder}>
                No quick actions available at this time.
            </Typography>
        );
    };

    return (
        <div className={classes.root}>
            {renderContent()}
        </div>
    );
};

export default connect()(withStyles(styles)(QuickActionCards));